import { useDisplayedCarouselItem } from '@/hooks/use-displayed-carousel-item';
import { WillBeNullProvider } from '@/providers/will-be-null';
import { scrollIntoView } from '@/utils/scroll';
import { EnumButtonName } from '@calm/evexia-analytics';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { CarouselStatusAnnouncer } from '@rhg/components/carousel/status-announcer';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Theme, SxProps, IconButton } from '@rhg/libs/material';
import {
    getValueByBreakpoint,
    NumberByBreakpoint,
    sxAppend,
} from '@rhg/libs/material/sx';
import {
    Children,
    ReactElement,
    ReactNode,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { CarouselItem } from './carousel-item';
import {
    navOffsetPercentPerSide,
    styleArrowBackIosCorrector,
    styleButton,
    styleCarouselContainer,
    styleContainerHoverNavDisplay,
    styleInnerCarouselContainer,
    styleNavArrowContainer,
    styleNavDisplayOnHover,
    styleOuterCarouselContainer,
} from './styles';

/**
 * Delay before the first programmatic auto-scroll and the gap between each
 * subsequent auto-scroll, in milliseconds.
 */
export const AUTO_SCROLL_INTERVAL_MS = 3500;

interface NavArrowsProps {
    childCount: number;
    showNavOnlyOnHover: boolean;
    constrainNavArrowsToVisibleItem: boolean;
    trackBackButton: EnumButtonName;
    trackForwardButton: EnumButtonName;
    onScrollBack: () => void;
    onScrollForward: () => void;
    gridContainerRef: RefObject<HTMLDivElement | null>;
    scrollTargetRef: RefObject<HTMLDivElement | null>;
}

const NavArrows = ({
    childCount,
    showNavOnlyOnHover,
    constrainNavArrowsToVisibleItem,
    trackBackButton,
    trackForwardButton,
    onScrollBack,
    onScrollForward,
    gridContainerRef,
    scrollTargetRef,
}: NavArrowsProps) => {
    const { formatMessage } = useIntl();
    const currentBreakpoint = useCurrentBreakpointName();
    const { visibleItemsIndex } = useDisplayedCarouselItem();
    const [hasInitializedNavArrows, setHasInitializedNavArrows] =
        useState(false);
    const [insetNavArrows, setInsetNavArrows] = useState(false);
    const [maxNavArrowContainerWidth, setMaxArrowContainerWidth] = useState(0);

    const isFirstScrollPosition = visibleItemsIndex?.includes(0);
    const isLastScrollPosition = visibleItemsIndex?.includes(childCount - 1);

    /**
     * The nav arrows are only rendered after the first calculation of visible items
     * We still want to render the container to avoid layout shift, but not the buttons
     */
    useEffect(() => {
        if (!hasInitializedNavArrows && Boolean(visibleItemsIndex?.length)) {
            setHasInitializedNavArrows(true);
        }
    }, [hasInitializedNavArrows, visibleItemsIndex?.length]);

    useEffect(() => {
        const handleResize = () => {
            const containerWidth = gridContainerRef.current?.offsetWidth;
            // Width without scrollbar
            const viewportWidth = window.document.body.clientWidth;
            const itemWidth = scrollTargetRef.current?.children[0].clientWidth;

            if (constrainNavArrowsToVisibleItem && itemWidth) {
                setMaxArrowContainerWidth(itemWidth);
            }

            setInsetNavArrows(containerWidth === viewportWidth);
        };

        handleResize(); // Check on initial render

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [constrainNavArrowsToVisibleItem, gridContainerRef, scrollTargetRef]);

    return (
        <Box
            className="navArrowContainer"
            sx={sxAppend(
                styleNavArrowContainer,
                {
                    justifyContent: isFirstScrollPosition
                        ? 'flex-end'
                        : isLastScrollPosition
                        ? 'flex-start'
                        : 'space-between',
                },
                insetNavArrows && {
                    width: '100%',
                    marginLeft: 0,
                    px: 2,
                },
                showNavOnlyOnHover && styleNavDisplayOnHover,
                constrainNavArrowsToVisibleItem &&
                    maxNavArrowContainerWidth > 0 && {
                        width: {
                            xs: `calc(${maxNavArrowContainerWidth}px + ${
                                navOffsetPercentPerSide * 4
                            }%)`,
                            md: `calc(${maxNavArrowContainerWidth}px + ${
                                navOffsetPercentPerSide * 2
                            }%)`,
                            xl: `calc(${maxNavArrowContainerWidth}px + ${navOffsetPercentPerSide}%)`,
                        },
                        justifySelf: 'center',
                    }
            )}
        >
            {hasInitializedNavArrows && !isFirstScrollPosition && (
                <IconButton
                    trackButton={trackBackButton}
                    onClick={onScrollBack}
                    aria-label={formatMessage(
                        commonMessages['common.previous']
                    )}
                    color="primary"
                    size={
                        currentBreakpoint === 'xs' || currentBreakpoint === 'sm'
                            ? 'small'
                            : 'legacyMedium'
                    }
                    sx={styleButton}
                >
                    <ArrowBackIos sx={styleArrowBackIosCorrector} />
                </IconButton>
            )}
            {hasInitializedNavArrows && !isLastScrollPosition && (
                <IconButton
                    trackButton={trackForwardButton}
                    aria-label={formatMessage(commonMessages['common.next'])}
                    color="primary"
                    size={
                        currentBreakpoint === 'xs' || currentBreakpoint === 'sm'
                            ? 'small'
                            : 'legacyMedium'
                    }
                    sx={styleButton}
                    onClick={onScrollForward}
                >
                    <ArrowForwardIos />
                </IconButton>
            )}
        </Box>
    );
};

interface DynamicCarouselProps {
    trackBackButton: EnumButtonName;
    trackForwardButton: EnumButtonName;
    trackItemScrollButton?: EnumButtonName;
    children?: ReactNode;
    /** Whether the carousel data has been fetched */
    isFetched?: boolean;
    /** Show navigation arrows only on hover */
    showNavOnlyOnHover?: boolean;
    /** Horizontal padding offset in pixels for the first item */
    firstItemOffsetPx?: NumberByBreakpoint;
    scrollContainerSx?: SxProps<Theme>;
    nonVisibleCarouselItemSx?: SxProps<Theme>;
    sx?: SxProps<Theme>;
    innerContainerSx?: SxProps<Theme>;
    /** Constrain navigation arrows to the visible item width */
    constrainNavArrowsToVisibleItem?: boolean;
    'data-testid'?: string;
    /** An element to show if the carousel contents are conditional and could all end up as null. **/
    emptyState?: ReactElement;
    id?: string;
    /**
     * Called once per mount the first time the carousel's last item enters
     * the visible window. `isAutoScroll` is true when the end was reached by
     * the programmatic {@link autoScroll} timer, and false when it was reached
     * by a user-initiated scroll (drag / wheel / swipe / nav-arrow click).
     */
    onScrolledToEnd?: (info: { isAutoScroll: boolean }) => void;
    /**
     * When true, the carousel automatically advances forward one step at a time
     * — equivalent to clicking the forward nav arrow — starting
     * {@link AUTO_SCROLL_INTERVAL_MS}ms after mount and repeating on that same
     * interval. The user can still scroll/drag in either direction at any time;
     * auto-scroll always advances from whatever item is currently visible. Once
     * the last item becomes visible, auto-scroll stops for the rest of the
     * mount (it can run again on a fresh mount / page visit).
     */
    autoScroll?: boolean;
}

export const DynamicCarousel = ({
    isFetched,
    children,
    firstItemOffsetPx = 0,
    showNavOnlyOnHover = false,
    constrainNavArrowsToVisibleItem = false,
    trackForwardButton,
    trackBackButton,
    scrollContainerSx,
    nonVisibleCarouselItemSx,
    sx,
    innerContainerSx,
    trackItemScrollButton,
    'data-testid': dataTestId,
    emptyState,
    id,
    onScrolledToEnd,
    autoScroll = false,
}: DynamicCarouselProps) => {
    const { visibleItemsIndex } = useDisplayedCarouselItem();
    const scrollTarget = useRef<HTMLDivElement>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const childCount = useMemo(
        () => Children.toArray(children).length,
        [children]
    );
    const hasInitializedRef = useRef(false);
    const hasFiredScrolledToEndRef = useRef(false);
    // Whether the auto-scroll timer has stopped for this mount (after reaching
    // the last item). Once true, the timer is never restarted for this mount.
    const autoScrollFinishedRef = useRef(false);
    // Whether the most recent scroll was triggered by the auto-scroll timer
    // rather than the user. Used to attribute `isAutoScroll` on onScrolledToEnd.
    const autoScrollTriggeredRef = useRef(false);
    // Holds the latest forward-scroll function so the (stable) auto-scroll
    // interval can always advance from the current visibility state.
    const scrollForwardRef = useRef<() => void>(() => {});
    // Whether auto-scroll is paused because the user is hovering or focused
    // within the carousel (WCAG 2.2.2 Pause, Stop, Hide). Resumes on leave/blur.
    const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
    // Set by a Back/Forward click or an auto-scroll tick, cleared by
    // CarouselStatusAnnouncer once it has announced the resulting position.
    // See CarouselStatusAnnouncer.
    const pendingAnnouncementRef = useRef(false);

    const isLastItemVisible = Boolean(
        visibleItemsIndex?.includes(childCount - 1)
    );

    useEffect(() => {
        if (hasFiredScrolledToEndRef.current) return;
        if (childCount === 0) return;
        if (!visibleItemsIndex || visibleItemsIndex.length === 0) return;
        if (!visibleItemsIndex.includes(childCount - 1)) return;
        hasFiredScrolledToEndRef.current = true;
        onScrolledToEnd?.({ isAutoScroll: autoScrollTriggeredRef.current });
    }, [visibleItemsIndex, childCount, onScrolledToEnd]);

    // On initial mount, prevent the browser from auto-snapping to the nearest item
    // which can end up centering the 2nd item. Temporarily disable snap, center the
    // first item horizontally, then re-enable snap.
    useEffect(() => {
        if (!isFetched || hasInitializedRef.current) return;

        const container = scrollTarget.current;
        if (!container) return;

        hasInitializedRef.current = true;

        const firstItem = container.children[0] as HTMLElement | undefined;
        if (firstItem) {
            scrollIntoView(container, firstItem, {
                onlyIfNotFullyVisible: false,
            });
        }
    }, [isFetched]);

    /**
     * This is used to properly align the first actual carousel item.
     * We do this by creating a blank invisible first item in the list with the specified width and padding.
     */
    const currentBreakpoint = useCurrentBreakpointName();
    const firstItemOffsetValue = useMemo(
        () => getValueByBreakpoint(firstItemOffsetPx, currentBreakpoint) ?? 0,
        [currentBreakpoint, firstItemOffsetPx]
    );

    const scrollHandler = useCallback(
        (isPrev = false) => {
            const carouselItems = scrollTarget.current?.children;

            if (carouselItems?.length && Array.isArray(visibleItemsIndex)) {
                const largestVisibleIndex = Math.max(...visibleItemsIndex);
                const smallestVisibleIndex = Math.min(...visibleItemsIndex);
                const prevIndex =
                    smallestVisibleIndex === 0
                        ? smallestVisibleIndex
                        : smallestVisibleIndex - 1;
                const nextIndex =
                    largestVisibleIndex === childCount
                        ? largestVisibleIndex
                        : largestVisibleIndex + 1;

                const elementToScroll =
                    scrollTarget.current?.children[
                        isPrev ? prevIndex : nextIndex
                    ];

                if (
                    scrollTarget.current &&
                    elementToScroll instanceof HTMLElement
                ) {
                    scrollIntoView(scrollTarget.current, elementToScroll);
                }
            }
        },
        [visibleItemsIndex, childCount]
    );

    // Keep the (stable) auto-scroll interval pointed at the latest forward
    // scroll so each tick advances from the current visibility state without
    // having to tear the interval down on every visibility change.
    useEffect(() => {
        scrollForwardRef.current = () => scrollHandler(false);
    }, [scrollHandler]);

    // Programmatic auto-scroll: advance forward on a fixed interval, starting
    // AUTO_SCROLL_INTERVAL_MS after the carousel becomes interactive and
    // repeating on that same cadence. Stops permanently once the last item is
    // visible. User scrolls are never blocked — each tick simply advances from
    // wherever the user currently is.
    useEffect(() => {
        if (!autoScroll || !isFetched) return;
        // Respect the user's OS-level motion preference (WCAG 2.3.3).
        if (
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            return;
        }
        if (autoScrollFinishedRef.current) return;
        // Paused while the user hovers / is focused within the carousel.
        if (isAutoScrollPaused) return;
        // Wait until intersection visibility has been established.
        if (!visibleItemsIndex?.length) return;
        if (childCount > 0 && isLastItemVisible) {
            autoScrollFinishedRef.current = true;
            return;
        }

        const intervalId = setInterval(() => {
            autoScrollTriggeredRef.current = true;
            // The visible card is about to swap with nothing else announced,
            // so let CarouselStatusAnnouncer pick up the new position.
            pendingAnnouncementRef.current = true;
            scrollForwardRef.current();
        }, AUTO_SCROLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [
        autoScroll,
        isFetched,
        childCount,
        isLastItemVisible,
        isAutoScrollPaused,
        visibleItemsIndex?.length,
    ]);

    // Pause auto-scroll while the user is interacting with the carousel
    // (hover or keyboard focus) and resume when they leave, per WCAG 2.2.2
    // (Pause, Stop, Hide). Touch/drag/wheel interactions are attributed
    // separately above and don't need to pause the cadence.
    useEffect(() => {
        if (!autoScroll) return;
        const container = gridContainerRef.current;
        if (!container) return;

        const pause = () => setIsAutoScrollPaused(true);
        const resume = () => setIsAutoScrollPaused(false);

        container.addEventListener('pointerenter', pause);
        container.addEventListener('pointerleave', resume);
        container.addEventListener('focusin', pause);
        container.addEventListener('focusout', resume);

        return () => {
            container.removeEventListener('pointerenter', pause);
            container.removeEventListener('pointerleave', resume);
            container.removeEventListener('focusin', pause);
            container.removeEventListener('focusout', resume);
        };
    }, [autoScroll]);

    // Attribute user-initiated scrolls (drag / wheel / touch) so the
    // `isAutoScroll` flag reported to onScrolledToEnd stays accurate.
    useEffect(() => {
        const container = scrollTarget.current;
        if (!container) return;

        const markUserInteraction = () => {
            autoScrollTriggeredRef.current = false;
        };

        container.addEventListener('pointerdown', markUserInteraction);
        container.addEventListener('wheel', markUserInteraction, {
            passive: true,
        });
        container.addEventListener('touchstart', markUserInteraction, {
            passive: true,
        });

        return () => {
            container.removeEventListener('pointerdown', markUserInteraction);
            container.removeEventListener('wheel', markUserInteraction);
            container.removeEventListener('touchstart', markUserInteraction);
        };
    }, []);

    if (!isFetched) {
        // TODO: Account for loading state
    }

    const arrayChildren = Children.toArray(children);
    const [nonNullChildren, setNonNullChildren] = useState(
        new Set(arrayChildren.keys())
    );

    const handleWillBeNull = (index: number) => {
        return () => {
            setNonNullChildren((childrenSet) => {
                childrenSet.delete(index);
                return new Set(childrenSet);
            });
        };
    };

    if (nonNullChildren.size === 0) {
        return <>{emptyState}</>;
    }

    return (
        <Box id={id} sx={sxAppend(styleOuterCarouselContainer, sx)}>
            <Box
                sx={sxAppend(
                    styleInnerCarouselContainer,
                    showNavOnlyOnHover && styleContainerHoverNavDisplay,
                    innerContainerSx
                )}
                ref={gridContainerRef}
            >
                <Box
                    data-testid={dataTestId}
                    role="list"
                    aria-roledescription="carousel"
                    sx={sxAppend(
                        styleCarouselContainer,
                        firstItemOffsetValue && {
                            px: `${firstItemOffsetValue}px`,
                        },
                        scrollContainerSx
                    )}
                    ref={scrollTarget}
                >
                    {arrayChildren
                        .filter((child) => child !== null)
                        .map((child, index) => {
                            return (
                                <WillBeNullProvider
                                    handleWillBeNull={handleWillBeNull(index)}
                                    key={index}
                                >
                                    <CarouselItem
                                        position={index}
                                        nonVisibleCarouselItemSx={
                                            nonVisibleCarouselItemSx
                                        }
                                        trackItemScrollButton={
                                            trackItemScrollButton
                                        }
                                        scrollContainerRef={scrollTarget}
                                    >
                                        {child}
                                    </CarouselItem>
                                </WillBeNullProvider>
                            );
                        })}
                </Box>
                <NavArrows
                    childCount={childCount}
                    showNavOnlyOnHover={showNavOnlyOnHover}
                    constrainNavArrowsToVisibleItem={
                        constrainNavArrowsToVisibleItem
                    }
                    trackBackButton={trackBackButton}
                    trackForwardButton={trackForwardButton}
                    onScrollBack={() => {
                        autoScrollTriggeredRef.current = false;
                        pendingAnnouncementRef.current = true;
                        scrollHandler(true);
                    }}
                    onScrollForward={() => {
                        autoScrollTriggeredRef.current = false;
                        pendingAnnouncementRef.current = true;
                        scrollHandler();
                    }}
                    gridContainerRef={gridContainerRef}
                    scrollTargetRef={scrollTarget}
                />
                <CarouselStatusAnnouncer
                    total={childCount}
                    pendingAnnouncementRef={pendingAnnouncementRef}
                />
            </Box>
        </Box>
    );
};
